
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface Referral {
  id: string;
  user_id: string;
  type: 'customer' | 'studio';
  invited_email: string;
  studio_name?: string;
  created_at: string;
  status: 'pending' | 'converted';
  converted_at: string | null;
}

export const useReferrals = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchReferrals = useCallback(async () => {
    if (!user) {
      setReferrals([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Safely map the raw data to our Referral type
      const processedReferrals: Referral[] = (data || []).map((item: any) => ({
        id: item.id || '',
        user_id: item.user_id || '',
        type: item.type || 'customer',
        invited_email: item.invited_email || '',
        studio_name: item.studio_name || undefined,
        created_at: item.created_at || new Date().toISOString(),
        status: item.status || 'pending',
        converted_at: item.converted_at || null
      }));

      setReferrals(processedReferrals);
    } catch (error: any) {
      console.error('Error fetching referrals:', error);
      toast({
        title: 'Fehler',
        description: 'Einladungen konnten nicht geladen werden.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const sendReferral = useCallback(async (email: string, type: 'customer' | 'studio', studioName?: string) => {
    if (!user) {
      toast({
        title: 'Fehler',
        description: 'Du musst angemeldet sein, um Einladungen zu versenden.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Insert into referrals table
      const referralPayload = {
        user_id: user.id,
        type,
        invited_email: email,
        studio_name: type === 'studio' ? studioName : null
      };

      const { data: insertedReferral, error: referralError } = await supabase
        .from('referrals')
        .insert(referralPayload as any)
        .select();

      if (referralError) throw referralError;

      // Invoke the cloud function to send the email
      const functionName = type === 'studio' ? 'send-studio-referral' : 'send-referral';
      const { error: functionError } = await supabase.functions.invoke(functionName, {
        body: {
          email,
          senderEmail: user.email,
          senderName: user.user_metadata?.full_name || user.email,
          studioName
        }
      });

      if (functionError) throw functionError;

      toast({
        title: 'Erfolg',
        description: `Einladung an ${email} versendet.`,
      });

      // Refresh the list
      fetchReferrals();
    } catch (error: any) {
      console.error('Error sending referral:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Einladung konnte nicht versendet werden.',
        variant: 'destructive'
      });
      throw error;
    }
  }, [user, fetchReferrals, toast]);

  return {
    referrals,
    loading,
    fetchReferrals,
    sendReferral
  };
};

export default useReferrals;
